import React, { useEffect, useRef, useState } from "react";
import useUserMedia from "../../hooks/useUserMedia";

/**
 * Usage:
 * <CameraCapture
 *   onCapture={(blob) => {...}}
 *   onClose={() => {...}}
 *   preferBackCamera // optional
 * />
 */
export default function CameraCapture({ onCapture, onClose, preferBackCamera = true }) {
  const initialMode = preferBackCamera ? "environment" : "user";
  const { stream, error, facingMode, switchCamera, request } = useUserMedia(initialMode);
  const videoRef = useRef(null);
  const canvasRef = useRef(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    const v = videoRef.current;
    if (!v || !stream) return;

    // Attach stream
    v.srcObject = stream;

    // iOS/iPadOS: must be muted + playsInline; call play() after canplay
    const onCanPlay = async () => {
      try {
        await v.play();
      } catch {
        // Some Safari versions require a user gesture; button click below will also call play()
      }
    };
    v.addEventListener("canplay", onCanPlay);
    return () => v.removeEventListener("canplay", onCanPlay);
  }, [stream]);

  const handleClickToStart = async () => {
    const v = videoRef.current;
    if (!v) return;
    try {
      await v.play();
    } catch (e) {
      // If still blocked, re-request stream (permissions/glitch)
      await request(facingMode);
    }
  };

  const takePhoto = () => {
    if (!videoRef.current || !canvasRef.current) return;
    const v = videoRef.current;
    const c = canvasRef.current;
    const w = v.videoWidth || 1280;
    const h = v.videoHeight || 720;
    c.width = w;
    c.height = h;
    const ctx = c.getContext("2d");
    ctx.drawImage(v, 0, 0, w, h);
    c.toBlob((blob) => {
      if (blob) onCapture?.(blob);
    }, "image/jpeg", 0.92);
  };

  return (
    <div className="w-full h-full flex flex-col gap-3 p-4">
      <div className="relative w-full aspect-video bg-black rounded-2xl overflow-hidden">
        <video
          ref={videoRef}
          className="absolute inset-0 w-full h-full object-cover"
          // iOS/iPadOS critical attributes:
          playsInline
          muted
          autoPlay
        />
        {!stream && (
          <div className="absolute inset-0 flex items-center justify-center text-white text-sm">
            {error ? "Camera error. Check permissions & HTTPS." : "Starting camera..."}
          </div>
        )}
      </div>

      {error && (
        <div className="text-red-600 text-sm">
          {String(error?.message || error)}
        </div>
      )}

      <div className="flex items-center gap-2">
        <button
          onClick={handleClickToStart}
          className="px-3 py-2 rounded-xl bg-gray-200 hover:bg-gray-300"
        >
          Start / Unblock
        </button>
        <button
          onClick={takePhoto}
          disabled={!stream || busy}
          className="px-3 py-2 rounded-xl bg-gray-900 text-white disabled:opacity-50"
        >
          Capture
        </button>
        <button
          onClick={switchCamera}
          className="px-3 py-2 rounded-xl bg-gray-200 hover:bg-gray-300"
        >
          Switch ({facingMode === "user" ? "Front" : "Back"})
        </button>
        <button
          onClick={onClose}
          className="ml-auto px-3 py-2 rounded-xl border border-gray-300 hover:bg-gray-50"
        >
          Close
        </button>
      </div>

      <canvas ref={canvasRef} className="hidden" />
    </div>
  );
}
